import React from 'react';

function InvestmentsAgriculture() {
  return (
    <main className="bg-white">
      {/* Hero */}
      <section className="relative h-[56vh] min-h-[420px] overflow-hidden">
        <img src="/images/agriculture/wheat-9666681_1280.jpg" alt="Agriculture" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/55 to-black/80" />
        <div className="relative z-10 h-full flex items-center">
          <div className="max-w-7xl mx-auto px-6">
            <h1 className="text-white text-4xl sm:text-5xl md:text-6xl font-light tracking-[-0.02em]">Agriculture</h1>
            <p className="mt-4 text-white/90 max-w-2xl text-lg">Sustainable food production, agri‑tech, and resilient supply chains.</p>
          </div>
        </div>
      </section>

      {/* Investment Thesis */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-semibold text-gray-900">Why Agriculture Now</h2>
            <p className="mt-3 text-gray-600 max-w-3xl mx-auto">Global population growth, climate volatility, and resource constraints are driving unprecedented demand for agricultural innovation and sustainable food systems.</p>
          </div>
          <div className="grid lg:grid-cols-2 gap-12 items-start mt-10">
            <div>
              <h3 className="text-2xl font-semibold text-gray-900 mb-6">The Agriculture Challenge</h3>
              <p className="text-gray-600 leading-relaxed mb-6">
                By 2050, the world will need to feed 10 billion people while addressing climate change, water scarcity, and declining arable land. Traditional farming methods cannot meet this demand. We invest in companies transforming agriculture through precision technology, sustainable practices, and supply chain innovation.
              </p>
              <p className="text-gray-600 leading-relaxed mb-6">
                Our agriculture portfolio spans the entire value chain: from farm inputs and precision equipment to storage, processing, and distribution. We prioritize solutions that increase yields, reduce environmental impact, and improve farmer economics while ensuring food security and traceability.
              </p>
              <div className="bg-green-50 border-l-4 border-green-600 p-5 rounded-r-lg">
                <p className="text-sm text-gray-700 leading-relaxed">
                  <strong className="text-green-700">Market Size:</strong> Global agriculture is a $5 trillion industry with agri-tech investments growing at 14% CAGR. Precision agriculture alone is projected to reach $12.9B by 2027.
                </p>
              </div>
            </div>
            <div className="relative">
              <img src="/images/agriculture/tomatoes-1280859_1280.jpg" alt="Agriculture thesis" className="w-full h-96 object-cover rounded-2xl shadow-xl" />
              <div className="absolute -bottom-5 -right-5 w-40 h-40 bg-green-600 opacity-10 rounded-3xl" />
            </div>
          </div>
          <div className="mt-12 grid md:grid-cols-3 gap-6">
            {[
              {title: 'Precision Farming', desc: 'IoT sensors, drones, satellite imagery, variable rate technology, and AI-driven crop management systems optimizing inputs and yields.'},
              {title: 'Sustainable Inputs', desc: 'Biological pesticides, precision fertilizers, soil microbiome enhancement, and regenerative agriculture practices reducing chemical use.'},
              {title: 'Supply Chain Tech', desc: 'Cold chain logistics, blockchain traceability, market access platforms, and agri-fintech solutions connecting farmers to markets.'}
            ].map((item, idx) => (
              <div key={idx} className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
                <h4 className="font-semibold text-gray-900 mb-2">{item.title}</h4>
                <p className="text-sm text-gray-600 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Value Chain */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-3xl font-semibold text-gray-900 text-center mb-10">Value Chain Opportunities</h2>
          <div className="grid md:grid-cols-3 gap-6">
            {[{t:'Inputs & Production',d:'Seeds, bio‑inputs and on‑farm automation for higher yields.',img:'/images/agriculture/apples-7346077_1280.jpg'},{t:'Processing & Storage',d:'Grain handling, milling, cold chain and packaging to reduce losses.',img:'/images/agriculture/silo-3412277_1280.jpg'},{t:'Distribution & Markets',d:'Digitized logistics, buyer networks and agri‑market platforms.',img:'/images/agriculture/watermelon-5461135_1280.jpg'}].map(x=> (
              <article key={x.t} className="rounded-2xl overflow-hidden bg-white border border-gray-100 shadow-sm">
                <img src={x.img} alt={x.t} className="w-full h-40 object-cover" />
                <div className="p-6">
                  <h3 className="text-lg font-semibold text-gray-900">{x.t}</h3>
                  <p className="mt-2 text-gray-600 text-sm">{x.d}</p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Investment Approach */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-semibold text-gray-900">Our Agriculture Investment Strategy</h2>
            <p className="mt-3 text-gray-600 max-w-3xl mx-auto">We combine deep agricultural expertise with technology assessment to identify scalable, sustainable solutions.</p>
          </div>
          <div className="grid md:grid-cols-2 gap-10">
            <div className="space-y-6">
              <div className="bg-gradient-to-br from-green-50 to-white p-6 rounded-xl border border-green-100">
                <h4 className="font-semibold text-gray-900 mb-3">Investment Criteria</h4>
                <ul className="space-y-2 text-sm text-gray-600">
                  <li className="flex gap-2"><span className="text-green-600">✓</span> Proven ROI for farmers with 2-3 year payback periods</li>
                  <li className="flex gap-2"><span className="text-green-600">✓</span> Scalable across geographies and crop types</li>
                  <li className="flex gap-2"><span className="text-green-600">✓</span> Environmental sustainability and carbon impact</li>
                  <li className="flex gap-2"><span className="text-green-600">✓</span> Strong IP, regulatory compliance, and distribution channels</li>
                  <li className="flex gap-2"><span className="text-green-600">✓</span> Management team with agriculture and tech expertise</li>
                </ul>
              </div>
              <div className="bg-gray-50 p-6 rounded-xl border border-gray-100">
                <h4 className="font-semibold text-gray-900 mb-3">Due Diligence Focus</h4>
                <ul className="space-y-2 text-sm text-gray-600">
                  <li>• Agronomic validation & field trial results</li>
                  <li>• Farmer adoption metrics & churn analysis</li>
                  <li>• Unit economics & path to profitability</li>
                  <li>• Supply chain resilience & regulatory landscape</li>
                  <li>• Climate risk & sustainability certifications</li>
                </ul>
              </div>
            </div>
            <div className="space-y-6">
              <img src="/new-assets/pexels-tima-miroshnichenko-7567598.jpg" alt="Agriculture investment" className="w-full h-64 object-cover rounded-2xl shadow-lg" />
              <div className="bg-gradient-to-br from-gray-900 to-gray-800 text-white p-6 rounded-xl">
                <h4 className="font-semibold mb-4">Value Creation Levers</h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  {['Go-to-market strategy', 'Strategic partnerships', 'R&D optimization', 'Geographic expansion', 'Product line extension', 'Exit positioning'].map(item => (
                    <div key={item} className="bg-white/10 backdrop-blur rounded-lg p-3">{item}</div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Impact */}
      <section className="py-16 bg-gradient-to-br from-green-900 to-green-800 text-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-semibold">Portfolio Impact</h2>
            <p className="mt-3 text-white/80 max-w-3xl mx-auto">Our agriculture investments deliver measurable outcomes for farmers, the environment, and food security.</p>
          </div>
          <div className="grid md:grid-cols-4 gap-6">
            {[
              {metric: '5M+', label: 'Hectares under management'},
              {metric: '22%', label: 'Average yield improvement'},
              {metric: '35%', label: 'Reduction in water usage'},
              {metric: '500K+', label: 'Smallholder farmers supported'}
            ].map((item, idx) => (
              <div key={idx} className="text-center bg-white/10 backdrop-blur rounded-xl p-6 border border-white/20">
                <div className="text-4xl font-bold text-yellow-400 mb-2">{item.metric}</div>
                <div className="text-sm text-white/80">{item.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h3 className="text-3xl font-semibold text-gray-900">Partner on Agricultural Investments</h3>
          <p className="mt-2 text-gray-600">Access our pipeline, due diligence frameworks, and agronomy expertise.</p>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <a href="/apply" className="px-6 py-3 rounded-md bg-gradient-to-r from-[#fbbf24] to-[#f59e0b] text-black font-semibold hover:shadow-lg transition-shadow">Apply now</a>
            <a href="/contact" className="px-6 py-3 rounded-md border-2 border-gray-300 text-gray-700 hover:border-gray-400 transition-colors">Contact us</a>
          </div>
        </div>
      </section>
    </main>
  );
}

export default InvestmentsAgriculture;


